import React, { useState } from 'react';
import { Send, MapPin, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { trackGenerateLead, trackWhatsAppClick } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppSVG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/api/contact`, form);
      trackGenerateLead('contact_form', { subject: form.subject });
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
    }
    catch { setError('Failed to send. Please try WhatsApp.'); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="section-padding relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 cosmic-bg" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-[120px]" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">Contact | संपर्क</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-6 tracking-tight">
            Get In <span className="text-gold-gradient">Touch</span>
          </h2>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info Cards */}
          <div className="lg:col-span-2 space-y-5">
            {/* WhatsApp */}
            <a href="https://wa.me/919311369729" target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('contact_section')}
              className="glass-card p-5 flex items-center gap-4 hover:border-green-500/30 transition-all group cursor-pointer block"
              data-testid="contact-whatsapp">
              <div className="w-12 h-12 rounded-xl bg-green-600/20 flex items-center justify-center border border-green-500/20 group-hover:bg-green-600/30 transition-colors">
                <WhatsAppSVG className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-text-muted">WhatsApp</p>
                <p className="text-text-main font-medium">+91 93113 69729</p>
              </div>
            </a>

            {/* Address */}
            <div className="glass-card p-5 flex items-start gap-4" data-testid="contact-address">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/15 to-purple/15 flex items-center justify-center border border-gold/10 flex-shrink-0">
                <MapPin className="w-6 h-6 text-gold" />
              </div>
              <div>
                <p className="text-xs text-text-muted mb-1">Address / पता</p>
                <p className="text-text-main font-medium text-sm leading-relaxed">
                  PB-77 GF Parklands Pride<br/>
                  Sector 77, Faridabad<br/>
                  Haryana, India
                </p>
              </div>
            </div>

            {/* Google Reviews CTA */}
            <a href="https://g.page/r/CbtzPR3gc6gNEBM/review" target="_blank" rel="noopener noreferrer"
              className="glass-card p-5 flex items-center gap-4 hover:border-blue-500/40 transition-all group cursor-pointer block"
              data-testid="contact-google-review"
              aria-label="Leave a Google Review for Vivek Mudgal Astrologer">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/30 group-hover:bg-blue-500/30 transition-colors flex-shrink-0">
                <svg width="22" height="22" viewBox="0 0 48 48" fill="#4285F4"><path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/></svg>
              </div>
              <div>
                <p className="text-xs text-text-muted">Google Business Profile</p>
                <p className="text-text-main font-medium">Leave a Google Review →</p>
              </div>
            </a>

            {/* Devotional Quote */}
            <div className="glass-card p-6 text-center border-gold/10">
              <p className="text-3xl text-gold/30 om-glow font-heading mb-3">ॐ</p>
              <p className="text-gold/50 font-heading italic text-sm leading-relaxed">
                "सर्वे भवन्तु सुखिनः सर्वे सन्तु निरामयाः"
              </p>
              <p className="text-text-muted/50 text-xs mt-2">
                May all be happy, may all be free from illness
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {success ? (
              <div className="glass-card p-10 text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="font-heading text-2xl text-text-main mb-2">Message Sent!</h3>
                <p className="text-text-muted mb-2">We'll get back to you soon.</p>
                <p className="text-text-muted/60 text-sm font-heading italic mb-6">हम जल्द ही आपसे संपर्क करेंगे।</p>
                <button onClick={() => setSuccess(false)} className="btn-gold-outline">Send Another</button>
              </div>
            ) : (
              <div className="glass-card p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-text-muted mb-2 font-medium">Name / नाम <span className="text-gold">*</span></label>
                      <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your full name" required data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2 font-medium">Email</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="your@email.com" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-text-muted mb-2 font-medium">Phone / फोन</label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91 XXXXX XXXXX" data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2 font-medium">Subject / विषय</label>
                      <select value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="contact-subject">
                        <option>General Inquiry</option>
                        <option>Kundli Analysis</option>
                        <option>Vastu Consultation</option>
                        <option>Career Guidance</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-2 font-medium">Message / संदेश <span className="text-gold">*</span></label>
                    <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help you..." rows={4} required data-testid="contact-message" />
                  </div>
                  {error && <p className="text-red-400 text-sm">{error}</p>}
                  <button type="submit" disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="contact-submit">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> Send Message / भेजें</>}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
